import type { z } from 'zod';
import type { WalletBalanceMessageDto, WalletDtoSchema } from '@/queries/wallet/wallet.schema';
import { useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/socket/useSocketEvent';
import openModal from '@/modals/utils/openModal';
import { QueriesKeys } from '@/queries/queriesKeys';
import { useAppSelector } from '@/redux/hooks';
import { WalletBalanceAutoTopupMessageSchema } from './wallet.schema';
import { createParsedEventHandler } from './wallet.sockets.helpers';

export function useWalletSocket() {
  const queryClient = useQueryClient();
  const userSession = useAppSelector(state => state.user.userSession);
  const isSessionActive = Boolean(userSession);

  const handleWalletBalanceAutoTopup = createParsedEventHandler(WalletBalanceAutoTopupMessageSchema, (data) => {
    if (data.currency === 'GC') {
      openModal('free-gold-coin', { topupAmount: data.topupAmount });
    }
  });

  // INFO: Disabled: In game full-screen mode, this pop-up overrides the fast shop
  // and breaks the gameplay. We only display it after exiting full-screen mode.
  // const handleWalletInsufficientBalance = createParsedEventHandler(WalletInsufficientBalanceMessageSchema, (data) => {
  //   if (data.currency === 'SC' || data.currency === 'BSC') {
  //     openModal('out-of-sweep-coin');
  //   }
  // });

  useSocketEvent(
    'connect',
    () => {
      queryClient.invalidateQueries({ queryKey: [QueriesKeys.wallet] });
    },
    isSessionActive,
  );

  useSocketEvent<WalletBalanceMessageDto>(
    'wallet.balance.update',
    (data) => {
      queryClient.setQueryData<z.infer<typeof WalletDtoSchema>>([QueriesKeys.wallet], (oldData) => {
        if (!oldData || !data) {
          return oldData;
        }
        const { sweepCoin, bonusSweepCoin } = data;

        const payload = {
          ...oldData,
          goldCoin: Number(data.goldCoin),
          sweepCoin: Number(sweepCoin),
          bonusSweepCoin: Number(bonusSweepCoin),
        };

        return payload;
      });
    },
    isSessionActive,
  );

  useSocketEvent('wallet.balance.auto-topup', handleWalletBalanceAutoTopup, isSessionActive);

  useSocketEvent('wallet.insufficient.balance', () => {
    // INFO: Disabled: In game full-screen mode, this pop-up overrides the fast shop
    // and breaks the gameplay. We only display it after exiting full-screen mode.

    // handleWalletInsufficientBalance(data)
  }, isSessionActive);
}
